import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, Star, Anchor, HardHat, Building2 } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Khan Kon Chi Construction & Development Corporation — concept site. Corporate
  infrastructure identity in the company's own blue. Imagery is limited to the
  logo, so the design leans on typography, real contract history and a
  restrained corporate layout rather than a large photo portfolio.
*/

const BLUE = "#1652A8";
const BLUE_DARK = "#0D3A7A";
const CREAM = "#F5F7FA";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/khankonchi.ph/";
const NAME = "Khan Kon Chi Construction & Development Corporation";
const SHORT = "Khan Kon Chi Construction";
const SLUG = "khan-kon-chi-construction-concept";
const P = "/construction/khankonchi";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function KhanKonChiConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Capabilities", "#capabilities"], ["Track Record", "#track-record"], ["Reviews", "#reviews"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="khankonchi-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Zilla+Slab:wght@500;600;700&display=swap');
        #top h1, #top h2, #top h3 { font-family: 'Zilla Slab', serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#08234C] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {SHORT}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-white transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="kkc-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={SHORT} className="h-11 w-11 object-contain" />
            <span className="text-base font-bold tracking-tight" style={{ color: BLUE_DARK }}>KHAN KON CHI CONSTRUCTION</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-medium" style={{ color: `${INK}99` }}>{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="kkc-header-cta" className="hidden sm:inline-flex rounded-sm px-5 py-2.5 text-sm font-semibold text-white" style={{ background: BLUE }}>
            Corporate Inquiry
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="kkc-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="kkc-mobile-cta" className="block mt-2 text-center rounded-sm px-5 py-3 text-sm font-semibold text-white" style={{ background: BLUE }}>Corporate Inquiry</a>
          </div>
        )}
      </header>

      {/* HERO — typography-led, restrained (logo is the only sourced imagery) */}
      <section className="relative py-12 md:py-16" style={{ background: BLUE_DARK }} data-testid="kkc-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_.42fr] border border-white/15">
          <motion.div {...fadeUp} className="p-8 sm:p-12 lg:p-16">
            <img src={`${P}/logo.jpg`} alt={SHORT} className="h-20 mb-8 bg-white rounded-lg p-2" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: "#9DC0F2" }}>Davao City &middot; 15 Years in Operation</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-white font-bold">Corporate &amp; Infrastructure Construction, at Scale.</h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl">
              A construction and development corporation delivering infrastructure and large-scale contract work across Mindanao.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="kkc-hero-cta" className="rounded-sm px-7 py-3.5 text-sm font-semibold text-white" style={{ background: BLUE }}>Corporate Inquiry</a>
              <a href="#track-record" data-testid="kkc-hero-track" className="rounded-sm border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">Our Track Record</a>
            </div>
            <p className="mt-8 text-sm text-white/60" data-testid="kkc-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: "#9DC0F2" }} /> 4.7 Google Rating · 24 Reviews
            </p>
          </motion.div>
          <aside className="p-8 sm:p-10 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-white/15" style={{ background: BLUE }}>
            <p className="text-xs uppercase tracking-[.22em] text-white/55">Documented operation</p>
            <div className="mt-14"><strong className="block text-7xl font-bold text-white">15</strong><span className="text-sm uppercase tracking-[.18em] text-white/70">Years in operation</span><p className="mt-8 text-sm text-white/70">4.7 Google Rating · 24 Reviews</p></div>
          </aside>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: BLUE }}>What We Do</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold leading-snug">Capabilities</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-10">
            {[
              [Anchor, "Port & Infrastructure", "Large-scale infrastructure work, including port-expansion and civil works contracts."],
              [Building2, "Corporate & Institutional", "Commercial and institutional construction delivered for corporate and government clients."],
              [HardHat, "Project Execution", "An active engineering and construction workforce equipped to run multiple concurrent contracts."],
            ].map(([Icon, t, b]) => (
              <motion.div key={t} {...fadeUp}>
                <Icon size={26} style={{ color: BLUE }} />
                <h3 className="mt-4 text-lg font-bold">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRACK RECORD */}
      <section id="track-record" className="py-16 md:py-20" style={{ background: BLUE }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
            <p className="text-2xl sm:text-3xl text-white font-semibold leading-snug">A Growing Port &amp; Marine-Infrastructure Portfolio.</p>
            <p className="mt-4 text-white/80 leading-relaxed">Public procurement and news records document major port and dredging contracts awarded across the Philippines.</p>
          </motion.div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              ["₱839.18M", "Currimao Port Expansion", "720-day contract awarded in 2024"],
              ["₱161.74M", "Masbate Port Expansion", "450-day contract awarded in 2024"],
              ["₱248.53M", "Iloilo Clustered Dredging", "Iloilo River and port-complex dredging project"],
            ].map(([value, title, detail]) => (
              <motion.article key={title} {...fadeUp} className="bg-white/10 border border-white/15 p-6 text-center">
                <p className="text-2xl font-bold text-white">{value}</p>
                <h3 className="mt-2 font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm text-white/65">{detail}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* PUBLIC REVIEW HIGHLIGHTS */}
      <section id="reviews" className="py-20 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: BLUE }}>Public Review Highlights</span>
            <h2 className="mt-3 text-3xl font-bold">What Reviewers Reported</h2>
          </motion.div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <motion.article {...fadeUp} className="border border-black/10 p-7">
              <p className="leading-relaxed text-black/75">Praised the workers as approachable and polite, and described the environment as clean.</p>
              <p className="mt-5 text-sm font-semibold" style={{ color: BLUE }}>Marianne B <a href="https://wanderlog.com/place/details/4394415/khan-kon-chi-construction--development-corporation" target="_blank" rel="noopener noreferrer" className="font-normal text-black/45 underline underline-offset-2">· Google review mirror · July 11, 2024 · 5/5</a></p>
            </motion.article>
            <motion.article {...fadeUp} className="border border-black/10 p-7">
              <p className="leading-relaxed text-black/75">A brief neutral review described the experience as nothing special.</p>
              <p className="mt-5 text-sm font-semibold" style={{ color: BLUE }}>Boy R <a href="https://wanderlog.com/place/details/4394415/khan-kon-chi-construction--development-corporation" target="_blank" rel="noopener noreferrer" className="font-normal text-black/45 underline underline-offset-2">· Google review mirror · June 3, 2024 · 3/5</a></p>
            </motion.article>
          </div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: BLUE }}>Get In Touch</span>
            <h2 className="mt-3 text-3xl font-bold leading-snug">Corporate Inquiry</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: BLUE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>ABG-DU30 Avenue, Km. 10.5, Purok 30, Cabantian, Buhangin, Davao City</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: BLUE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+63822917883" className="hover:underline">(082) 291 7883</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: BLUE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="kkc-facebook-link" className="hover:underline">facebook.com/khankonchi.ph</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="kkc"
              accent={BLUE}
              accentText="#FFFFFF"
              heading="Corporate Inquiry"
              subheading="Infrastructure or corporate construction — tell us about the contract and our team will follow up."
              projectTypes={["Infrastructure / Port Works", "Corporate / Institutional Building", "Other"]}
              phone="+63822917883"
              phoneDisplay="(082) 291 7883"
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: BLUE_DARK, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={SHORT} className="h-10 w-10 object-contain bg-white rounded p-1" />
              <span className="text-white text-base font-bold">KHAN KON CHI CONSTRUCTION</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Corporate and infrastructure construction based in Davao City.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">ABG-DU30 Avenue, Km. 10.5,<br />Cabantian, Buhangin, Davao City</p>
            <p className="text-sm mt-2">(082) 291 7883</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {SHORT}. Contract details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-sm px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: BLUE }}>
        Message {SHORT}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
